import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {HOTB} from "../HOTB";
import {GameConfig} from "../GameConfig";
import {AvalancheReelModel} from "./AvalancheReelModel";
import {ISymbol} from "../../core/reel/IReelData";
import {GameUtils} from "../utils/GameUtils";
import {CustomBounce} from "gsap/dist/CustomBounce";
import {gsap} from "gsap";
import {ExtendedGameModel} from "../server/ExtendedGameModel";
import {HOTBGameData, HOTBInitData} from "../server/HOTBData";
import {ExtendedGameMode} from "../server/ExtendedGameMode";
import {GameSymbol, SymbolsList} from "./GameSymbol";

export class ReelArea extends PIXI.Container implements StageLayer {
    private _satanSymbol: PIXI.Sprite;

    constructor() {
        super();
        this.name = "ReelArea";
        this.init();
    }

    private _noOfReelsCollapsed: number = 0;

    get noOfReelsCollapsed(): number {
        return this._noOfReelsCollapsed;
    }

    set noOfReelsCollapsed(value: number) {
        this._noOfReelsCollapsed = value;
    }

    private _lastReelOfFillUp: number = -1;

    get lastReelOfFillUp(): number {
        return this._lastReelOfFillUp;
    }

    set lastReelOfFillUp(value: number) {
        this._lastReelOfFillUp = value;
    }

    private _satanSymbolVisible: boolean = false;

    get satanSymbolVisible(): boolean {
        return this._satanSymbolVisible;
    }

    set satanSymbolVisible(value: boolean) {
        this._satanSymbolVisible = value;
    }

    private _mainReelModels: AvalancheReelModel[];
    get mainReelModels(): AvalancheReelModel[] {
        return this._mainReelModels;
    }

    public getAllReelCollapseDuration(): number {
        //set to 0 if actual is needed, 0.1 for buffer
        // let delay: number = 0.1;
        let delay: number = 0;

        this.mainReelModels.forEach((reel: AvalancheReelModel) => {
            if (reel.reelView.needCollapse) {
                delay += GameConfig.collapseReelDelay;
            }
        });

        return delay;
    }

    public collapse(): gsap.core.Timeline[] {
        const animations: gsap.core.Timeline[] = [];
        let delay: number = 0;
        this.mainReelModels.forEach((reel: AvalancheReelModel) => {
            if (reel.reelView.needCollapse) {
                animations.push(reel.reelView.collapseReel(delay));
                delay += GameConfig.collapseReelDelay;
            }
        });
        return animations;
    }

    public fillUp(): gsap.core.Timeline[] {
        const animations: gsap.core.Timeline[] = [],
            nearWinReels: boolean[] = HOTB.model.nearWinReels,
            nearWinDelay: number = GameConfig.nearWinDelay,
            gameData: HOTBGameData = HOTB.model.gameData,
            dropInDelay: number = GameUtils.isFastSpin() ? GameConfig.reelFillUpStopDelayFastSpin : GameConfig.reelFillUpStopDelay;

        HOTB.reelArea.lastReelOfFillUp = -1;

        for (let reelId: number = gameData.symbolsAfterAvalanchePrevSpin.length - 1; reelId >= 0; reelId--) {
            if (!(gameData.symbolsAfterAvalanchePrevSpin[reelId].includes(SymbolsList.S1))) {
                for (let symId: number = 0; symId < gameData.symbolsAfterAvalanchePrevSpin[reelId].length; symId++) {
                    if (gameData.symbolsAfterAvalanchePrevSpin[reelId][symId] === SymbolsList.BLANK) {
                        this.lastReelOfFillUp = reelId;
                        break;
                    }
                }
            }

            if (this.lastReelOfFillUp === reelId) {
                break;
            }
        }

        let delay: number = 0,
            scatterDelay: number = 0;

        this.mainReelModels.forEach((reel: AvalancheReelModel, i: number): void => {
            if (reel.reelView.needFillUp) {
                animations.push(reel.reelView.getFillUpReelAnimation(delay));
                if (GameConfig.TOTAL_SCATTER_COUNT >= 2 && !HOTB.model.data.featureBuyName.includes(ExtendedGameMode.FREESPIN)) {
                    scatterDelay += GameUtils.isFastSpin() ? GameConfig.scatterAnticipationDelayFastSpin : GameConfig.scatterAnticipationDelay;
                    if(GameConfig.TOTAL_SCATTER_COUNT === 4){
                        scatterDelay = 0;
                    }
                }
                delay += dropInDelay + (nearWinReels[i] ? nearWinDelay : 0) + scatterDelay;
            }
        });

        return animations;
    }

    public getSymbolAtPosition(reel: number, position: number): ISymbol {
        return this.mainReelModels[reel].getSymbol(position);
    }

    public toggleDisplay(show: boolean): void {
        this.mainReelModels.forEach((currReel: AvalancheReelModel): void => {
            currReel.reelView.visible = show;
        });
    }

    public stopAllBobbling(): void {
        this.mainReelModels.forEach((currReel: AvalancheReelModel): void => {
            for (let i: number = 0; i < currReel.reelView.children.length; i++) {
                const currSymbol: GameSymbol = <GameSymbol>currReel.reelView.children[i];
                currSymbol.resetBobble();
            }
        });
    }

    public toggleSatanSymbol(show: boolean): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        tl.add((): void => {
            this.satanSymbolVisible = show;
        });

        tl.to(this._satanSymbol.position, {
            y: show ? 170 : -300,
            duration: GameConfig.SATAN_SPIN_SYMBOL_DROP_TIME,
            ease: CustomBounce.create("myBounce", {
                strength: 0.2,
                endAtStart: false,
                squash: 1,
                squashID: "myBounce-squash"
            })
        });

        return tl;
    }

    public reInit(): void {
        this.removeChildren();
        this.init();
    }

    private init(): void {
        this._mainReelModels = [];
        let tempReel: string[],
            gameModel: ExtendedGameModel = HOTB.model,
            data: HOTBInitData | HOTBGameData = gameModel.data,
            reelData: string[][] = gameModel.isActionSpinToBonus ? data.evaluatedArea : data.reels;

        for (let i: number = 0; i < GameConfig.defaultGameAreaSize.length; i++) {
            tempReel = [];
            const reelModel: AvalancheReelModel = new AvalancheReelModel(i);
            this._mainReelModels.push(reelModel);
            this.addChild(reelModel.reelView);
            for (let j: number = 0; j < reelData[i].length; j++) {
                tempReel.push(reelData[i][j]);
            }
            reelModel.init(tempReel);
        }
        const mainReelsMask: PIXI.Graphics = new PIXI.Graphics();
        mainReelsMask.name = "reelAreaMask";
        mainReelsMask.beginFill(0xFFFF0B, 0.5);
        mainReelsMask.drawRect(0, 0, this.getReelAreaWidth() + 10, this.getReelAreaHeight() + 10);
        mainReelsMask.position.set(-55, -75);

        this.addChild(mainReelsMask);
        this.mask = mainReelsMask;
        this.position.set(159, 288);
        this.createSatanSymbol();
    }

    private getReelAreaWidth(): number {
        return (GameConfig.symbolWidth * GameConfig.totalReels);
    }

    private getReelAreaHeight(): number {
        return (GameConfig.symbolHeight * GameConfig.totalSymsPerReel) + 10;
    }

    private createSatanSymbol(): void {
        this._satanSymbol = new PIXI.Sprite(HOTB.assetLoader.getTextures("satanSpinBigSymbol")[0]);
        this._satanSymbol.name = "bigSatanSymbol";
        this._satanSymbol.anchor.set(0.5);
        this._satanSymbol.scale.set(0.975);
        this._satanSymbol.position.set(200, -300);
        this.addChild(this._satanSymbol);
    }
}